import {
  Bookmark,
  ListPlus,
  ThumbsUp
} from '@phosphor-icons/react'
import { Box } from '@mui/material'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { getContentImageUrl, THUMBNAIL_SIZES } from '../../../helpers/content-image'
import { contentUrl } from '../../../helpers/url'
import { useFormFactor } from '../../../hooks'
import { useDisplayAddToPlaylist } from '../../../hooks/useDisplayAddToPlaylist'
import { ActionButton, BookmarkButton } from '../../action-buttons'
import { useAddToBookmarks } from '../../add-to-bookmarks'
import { useLike } from '../../like'
import { UserGroupPolicy } from '../../user-group-policy'
import {
  ALLOW_LIKE_GROUP,
  ALLOW_PLAYLISTS_GROUP
} from '../../user-group-policy/permissions'
import { MtLogo } from './../../logos'

export const PROMOTED_CARD_TYPES = {
  MAIN: 'MAIN',
  SECONDARY: 'SECONDARY',
  GENERIC: 'GENERIC',
  DISCOVER: 'DISCOVER'
}

// Every promoted card shows the image at 16:9 (the ratio editors upload and
// crop at), so ask imgix for 16:9 too instead of pre-cropping it. About 2x
// each card's widest box: the hero is ~635px, the vertical and Discover cards
// ~300px, the compact card 107px.
const IMAGE_SIZES = {
  [PROMOTED_CARD_TYPES.MAIN]: { width: 1280, height: 720 },
  [PROMOTED_CARD_TYPES.SECONDARY]: THUMBNAIL_SIZES.CARD,
  [PROMOTED_CARD_TYPES.DISCOVER]: THUMBNAIL_SIZES.CARD,
  [PROMOTED_CARD_TYPES.GENERIC]: { width: 320, height: 180 }
}

const usePromotedCardMethods = ({
  content,
  size,
  position,
  cardType,
  onSelect,
  onAddToPlaylist,
  // Where the card sits, for the content URL's `from` and the bookmark's
  // attribution. The article's "Discover more content" passes its own.
  source = 'promoted',
  bookmarkFrom = 'promoted-content'
}) => {
  const { isDesktop } = useFormFactor()

  const cardSize = size || (isDesktop ? 'large' : 'mobile')
  const displayAddToPlaylist = useDisplayAddToPlaylist()

  const { isBookmarked, handleBookmark, bookmarking } = useAddToBookmarks({
    from: bookmarkFrom
  })
  const {
    handleLike: onLike,
    liking,
    isLiked
  } = useLike({ from: 'promoted-content' })
  const hasRecommend = isLiked(content || {})
  const isLiking = liking(content || {})

  const isBookmarking = bookmarking(content || {})
  const bookmarked = isBookmarked(content || {})

  // The button stays enabled while saving (disabling it would drop keyboard
  // focus), so a second click is ignored here instead of sending twice.
  const onBookmark = () => {
    if (!content || isBookmarking) return
    handleBookmark(content)
  }

  const contentImage = getContentImageUrl(content?.image, { ...IMAGE_SIZES[cardType], fallback: MtLogo })

  const navigate = useNavigate()
  const handleCardSelect = () => {
    if (!content) return
    if (onSelect) {
      onSelect(content, position)
    }
    navigate(contentUrl(content, source, position))
  }

  const handleLike = () => onLike(content)

  const handleAddToPlaylist = () => {
    onAddToPlaylist(content)
  }

  const actions = {
    leftActions: (
      <Box
        display='flex'
        flexDirection='row'
        alignItems='center'
        mt={1}
        gap={1}
      >
        <UserGroupPolicy
          accessGroup={ALLOW_LIKE_GROUP}
          componentAllow={
            <ActionButton
              size='xs'
              variant={hasRecommend ? 'secondary' : 'secondary-noselect'}
              label={hasRecommend ? 'Liked' : 'Like'}
              startIcon={
                <ThumbsUp size={18} weight={hasRecommend ? 'fill' : 'duotone'} />
              }
              onClick={handleLike}
              loading={isLiking}
              hideText={cardType !== PROMOTED_CARD_TYPES.MAIN}
              aria-busy={isLiking}
              aria-label={hasRecommend ? 'Unlike content' : 'Like content'}
            />
          }
        />
        <BookmarkButton
          bookmarked={bookmarked}
          variant={bookmarked ? 'secondary' : 'secondary-noselect'}
          size='xs'
          hideText={cardType !== PROMOTED_CARD_TYPES.MAIN}
          loading={isBookmarking}
          onClick={onBookmark}
          startIcon={<Bookmark size={18} weight={bookmarked ? 'fill' : 'regular'} />}
          aria-busy={isBookmarking}
          aria-label={bookmarked ? 'Remove from bookmarks' : 'Add to bookmarks'}
        />
        {displayAddToPlaylist && (
          <UserGroupPolicy
            accessGroup={ALLOW_PLAYLISTS_GROUP}
            componentAllow={
              <ActionButton
                variant='secondary-noselect'
                size='xs'
                label='Add to Playlist'
                startIcon={<ListPlus size={18} weight='duotone' />}
                onClick={handleAddToPlaylist}
                // Icon-only, like Like and Bookmark, where the label doesn't
                // fit: the compact card's text column, and the vertical cards
                // below lg (~205px wide at 900px).
                hideText={cardType === PROMOTED_CARD_TYPES.GENERIC || (cardType === PROMOTED_CARD_TYPES.SECONDARY && !isDesktop)}
              />
            }
          />
        )}
      </Box>
    )
  }

  return {
    contentImage,
    handleCardSelect,
    cardSize,
    actions,
    // For cards that lay their own actions out (DiscoverCard) instead of
    // rendering `actions`
    bookmark: { bookmarked, isBookmarking, onBookmark },
    displayAddToPlaylist,
    handleAddToPlaylist
  }
}

export default usePromotedCardMethods
